/**
 * cambios.js
 * Compara el residente tal como se cargó (original) con lo que quedó en el
 * asistente (actual) y devuelve la lista de cambios para el resumen de Editar.
 * Cada cambio: { seccion, tipo: "agregado" | "quitado" | "modificado", texto, antes?, despues? }
 */

const t = (v) => String(v ?? "").trim();
const vacio = (v) => (t(v) === "" || t(v) === "-" ? "" : t(v));

export function nombrePersona(p) {
  if (!p) return "";
  return p.es_sociedad
    ? t(p.nombre_sociedad)
    : `${t(p.nombre)} ${t(p.apellidos)}`.trim();
}

const placas = (p) =>
  (p?.vehiculos || [])
    .filter((v) => t(v.placa))
    .map((v) => `${t(v.placa).toUpperCase()}${v.extranjera ? " (extranjera)" : ""}`);

const emails = (p) =>
  p?.sin_correo
    ? []
    : [p?.emailPrincipal, ...(p?.emailExtras || []).map((e) => e.email)].map(t).filter(Boolean);

function campo(lista, seccion, etiqueta, antes, despues) {
  if (vacio(antes) !== vacio(despues)) {
    lista.push({
      seccion, tipo: "modificado", texto: etiqueta,
      antes: vacio(antes) || "(vacío)", despues: vacio(despues) || "(vacío)",
    });
  }
}

function listas(lista, seccion, etiqueta, antes, despues) {
  const a = new Set(antes);
  const d = new Set(despues);
  despues.filter((x) => !a.has(x)).forEach((x) =>
    lista.push({ seccion, tipo: "agregado", texto: `${etiqueta}: ${x}` }));
  antes.filter((x) => !d.has(x)).forEach((x) =>
    lista.push({ seccion, tipo: "quitado", texto: `${etiqueta}: ${x}` }));
}

function personaCambios(lista, seccion, o, a, extras = []) {
  campo(lista, seccion, "Tipo", o.es_sociedad ? "Sociedad" : "Persona física",
        a.es_sociedad ? "Sociedad" : "Persona física");
  campo(lista, seccion, "Cédula", o.cedula, a.cedula);
  campo(lista, seccion, "Nombre", nombrePersona(o), nombrePersona(a));
  extras.forEach(([k, etiqueta]) => campo(lista, seccion, etiqueta, o[k], a[k]));
  listas(lista, seccion, "Placa", placas(o), placas(a));
}

// Personas de una lista (copropietarios / autorizados), emparejadas por dbId.
function grupoCambios(lista, seccion, originales, actuales, extras) {
  const porId = new Map(originales.filter((x) => x.dbId).map((x) => [x.dbId, x]));
  const vistos = new Set();
  actuales.forEach((a) => {
    const o = a.dbId ? porId.get(a.dbId) : null;
    if (!o) {
      lista.push({ seccion, tipo: "agregado", texto: nombrePersona(a) || t(a.cedula) });
      return;
    }
    vistos.add(a.dbId);
    const sub = [];
    personaCambios(sub, seccion, o, a, extras);
    sub.forEach((c) => lista.push({ ...c, texto: `${nombrePersona(a)} - ${c.texto}` }));
  });
  originales.filter((o) => o.dbId && !vistos.has(o.dbId)).forEach((o) =>
    lista.push({ seccion, tipo: "quitado", texto: nombrePersona(o) || t(o.cedula) }));
}

export function calcularCambios(original, actual) {
  const lista = [];
  const f0 = original.filial;
  const f1 = actual.filial;
  campo(lista, "Filial", "ID Filial", f0.idFilial, f1.idFilial);
  campo(lista, "Filial", "N° Plano", f0.numPlano, f1.numPlano);
  campo(lista, "Filial", "N° Folio", f0.numFolio, f1.numFolio);
  campo(lista, "Filial", "Tipo de propiedad", f0.tipoPropiedad, f1.tipoPropiedad);

  const p0 = original.propietario;
  const p1 = actual.propietario;
  personaCambios(lista, "Propietario", p0, p1, [
    ["telefono_casa", "Teléfono casa"],
    ["telefono_cel", "Teléfono celular"],
    ["telefono_trabajo", "Teléfono trabajo"],
    ["notas", "Notas"],
  ]);
  campo(lista, "Propietario", "Correo", p0.sin_correo ? "No tiene correo" : "Con correo",
        p1.sin_correo ? "No tiene correo" : "Con correo");
  campo(lista, "Propietario", "Correo principal", emails(p0)[0], emails(p1)[0]);
  listas(lista, "Propietario", "Correo adicional", emails(p0).slice(1), emails(p1).slice(1));

  grupoCambios(lista, "Copropietarios", original.copropietarios, actual.copropietarios, [
    ["telefono_casa", "Teléfono casa"],
    ["telefono_cel", "Teléfono celular"],
    ["telefono_trabajo", "Teléfono trabajo"],
    ["notas", "Notas"],
  ]);
  grupoCambios(lista, "Autorizados", original.autorizados, actual.autorizados, [
    ["relacion", "Relación"],
  ]);
  return lista;
}
